import React, { useEffect, useState } from "react";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import {
  formatPhoneNumber,
  displayPhoneNumber,
  cleanPhoneNumber,
} from "../../../utils/phoneUtils";

export interface InputProps {
  id?: string;
  name?: string;
  type?:
    | "text"
    | "email"
    | "tel"
    | "password"
    | "number"
    | "url"
    | "date"
    | "month"
    | "time";
  value: string | number;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  className?: string;
  label?: string;
  error?: string;
  min?: number;
  max?: number;
  step?: number;
  autoComplete?: string;
  autoFocus?: boolean;
}

export const Input: React.FC<InputProps> = ({
  id,
  name,
  type = "text",
  value,
  onChange,
  onKeyDown,
  onFocus,
  onBlur,
  placeholder,
  disabled = false,
  required = false,
  className = "",
  label,
  error,
  min,
  max,
  step,
  autoComplete,
  autoFocus = false,
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const isPassword = type === "password";
  const effectiveType = isPassword ? (showPassword ? "text" : "password") : type;

  const baseClasses =
    "w-full px-4 py-3 rounded-xl bg-white dark:bg-[#2D3748] text-gray-900 dark:text-slate-200 placeholder-gray-500 dark:placeholder-slate-500 focus:ring-2 transition-colors duration-200 disabled:bg-gray-50 dark:disabled:bg-slate-700 disabled:cursor-not-allowed";

  const borderClasses = error
    ? "border border-red-300 dark:border-red-700 focus:ring-red-500 focus:border-red-500 dark:focus:ring-red-500 dark:focus:border-red-500"
    : "border border-gray-300 dark:border-[#374151] focus:ring-sky-500 dark:focus:ring-[#63B3ED] focus:border-sky-500 dark:focus:border-[#63B3ED]";

  const combinedClasses = `${baseClasses} ${borderClasses} ${className}`.trim();

  // Format prefilled phone numbers when component mounts or value changes
  useEffect(() => {
    if (type === "tel" && value && typeof value === "string") {
      const cleanValue = cleanPhoneNumber(value);
      const currentCleanValue = cleanPhoneNumber(value);

      // Only format if the value is unformatted (no parentheses or dashes)
      if (
        cleanValue.length >= 10 &&
        !value.includes("(") &&
        !value.includes("-")
      ) {
        const formatted = displayPhoneNumber(value);
        if (formatted !== value) {
          const syntheticEvent = {
            target: { value: formatted },
          } as React.ChangeEvent<HTMLInputElement>;
          onChange(syntheticEvent);
        }
      }
    }
  }, [value, type, onChange]);

  // Handle phone number formatting for tel inputs during typing
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (type === "tel") {
      const formatted = formatPhoneNumber(e.target.value);
      const syntheticEvent = {
        ...e,
        target: {
          ...e.target,
          value: formatted,
        },
      };
      onChange(syntheticEvent);
    } else {
      onChange(e);
    }
  };

  // Handle phone number formatting on blur (when complete)
  const handleInputBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    // Call the original onBlur if provided
    if (onBlur) {
      onBlur(e);
    }
  };

  return (
    <div className="w-full">
      {label && (
        <label
          htmlFor={id}
          className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2"
        >
          {label}
          {required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      <div className={isPassword ? "relative" : ""}>
        <input
          id={id}
          name={name}
          type={effectiveType}
          value={value}
          onChange={handleInputChange}
          onKeyDown={onKeyDown}
          onFocus={onFocus}
          onBlur={handleInputBlur}
          placeholder={placeholder}
          disabled={disabled}
          required={required}
          min={min}
          max={max}
          step={step}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          className={isPassword ? `${combinedClasses} pr-10` : combinedClasses}
        />
        {isPassword && (
          <button
            type="button"
            onClick={() => setShowPassword((p) => !p)}
            tabIndex={-1}
            disabled={disabled}
            className="absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded-lg text-gray-500 dark:text-slate-400 hover:text-gray-700 dark:hover:text-slate-200 hover:bg-gray-100 dark:hover:bg-[#374151] transition-colors disabled:opacity-50 disabled:pointer-events-none"
            aria-label={showPassword ? "Ocultar senha" : "Mostrar senha"}
          >
            {showPassword ? (
              <VisibilityOff sx={{ fontSize: 20 }} />
            ) : (
              <Visibility sx={{ fontSize: 20 }} />
            )}
          </button>
        )}
      </div>
      {error && (
        <p className="mt-2 text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
          <span className="text-red-500">⚠️</span>
          {error}
        </p>
      )}
    </div>
  );
};
